/* eslint-disable camelcase */
import { useContext, useMemo } from 'react';
import { useFragment, useReactiveVar } from '@apollo/client';

import { deliveryIDVar } from 'app/apollo/reaction';
import {
    LocationContext, resolveLocation, resolveLocationKey,
} from 'app/containers/LocationProvider';
import { DELIVERY_LOCATION_FRAGMENT } from 'app/graphql/network/fragments/deliveryLocation.fragment';
import { PathSegmentContext } from 'app/views/Main/PathSegment.context';

const getDeliveryIdFromPathname = (pathname) => pathname
    .split('/')
    .find((part) => Boolean(part) && !Number.isNaN(Number(part)));

/*
    Регион конкретной доставки (меню доставки — /app/delivery/:id).
    В кэше лежит в userQuery -> deliveries -> fullAddress
*/
export const useDeliveryLocationKey = () => {
    const deliveryIdFromVar = useReactiveVar(deliveryIDVar);

    const deliveryId = useMemo(
        () => deliveryIdFromVar || getDeliveryIdFromPathname(window.location.pathname),
        [deliveryIdFromVar],
    );

    const { data } = useFragment({
        fragment: DELIVERY_LOCATION_FRAGMENT,
        from: { __ref: `delivery:${deliveryId}` },
    });

    return useMemo(() => {
        const { country_subdivision: subdivision, city } = data?.fullAddress || {};

        if (subdivision) return resolveLocation(subdivision);
        if (city) return resolveLocationKey(city);

        return null;
    }, [data]);
};

/*
    Локация, по которой применяются региональные ограничения категорий
    (например, "Готовые блюда" только для МСК).
    В меню доставки — адрес самой доставки, в обычном меню — адрес из сессии.
*/
export const useCategoryLocationKey = () => {
    const { locationKey: sessionLocationKey } = useContext(LocationContext) || {};
    const { segment } = useContext(PathSegmentContext) || {};
    const deliveryLocationKey = useDeliveryLocationKey();

    return useMemo(
        () => (segment === 'delivery' ? deliveryLocationKey || sessionLocationKey : sessionLocationKey),
        [segment, deliveryLocationKey, sessionLocationKey],
    );
};
